import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { navForRole } from "@/components/roleNav";
import { managerService } from "@/services/paymentService";
import { documentService } from "@/services/documentService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhoneField } from "@/components/PhoneField";
import { ClientFilters, EMPTY_FILTERS, cleanFilters } from "@/components/ClientFilters";
import { ClientStatusCell } from "@/components/StatusBadge";
import { crmService } from "@/services/crmService";

const input = "h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white";
const card = "rounded-md border border-[#22252F] bg-[#12141A]";

const emptyForm = { name: "", email: "", password: "", phone_dial: "+55", phone: "", virtual_balance: 0, agent_id: "", manager_id: "" };

export default function ManagerPage() {
  const [caps, setCaps] = useState(null);
  const [tab, setTab] = useState("clients");
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [client, setClient] = useState(null);
  const [dialog, setDialog] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [meta, setMeta] = useState(null);
  const [mode, setMode] = useState("email");
  const [busy, setBusy] = useState(false);
  const [managers, setManagers] = useState([]);
  const [balanceClient, setBalanceClient] = useState(null);
  const [trading, setTrading] = useState(null);
  const [editPos, setEditPos] = useState(null);
  const [adjust, setAdjust] = useState({ amount: "", target: "balance", note: "", kind: "real" });

  const isAgent = caps?.role === "agent";
  const isSupervisor = caps?.role === "supervisor";
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const c = caps || (await managerService.capabilities());
      if (!caps) setCaps(c);
      setClients(await managerService.clients(cleanFilters(filters)));
      setSummary(await managerService.summary());
      if (c.can_create_agents) setAgents(await managerService.agents());
      if (c.can_create_managers) setManagers(await managerService.managers());
      if (tab === "deposits") setRows(await managerService.deposits());
      if (tab === "withdrawals") setRows(await managerService.withdrawals());
      if (tab === "trades" && client) {
        setRows(await managerService.trades(client));
        if (caps?.can_edit_positions) setTrading(await managerService.tradingDetail(client));
      }
      if (tab === "documents") setRows((await documentService.adminList({ page: 1, per_page: 50 })).items);
    } catch (e) { toast.error(errorMessage(e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, client, caps, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { crmService.meta().then(setMeta).catch(() => {}); }, []);

  const TABS = [
    { key: "clients", label: "Clientes" },
    ...(caps?.can_create_agents ? [{ key: "team", label: "Equipa" }] : []),
    { key: "trades", label: "Trading" },
    { key: "deposits", label: "Depósitos" },
    { key: "withdrawals", label: "Retiradas" },
    { key: "documents", label: "Documentos" },
  ];

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        password: form.password,
        ...(mode === "phone" ? { phone_dial: form.phone_dial, phone: form.phone } : { email: form.email }),
      };
      if (dialog === "client") {
        payload.virtual_balance = Number(form.virtual_balance) || 0;
        if (form.agent_id) payload.agent_id = form.agent_id;
        if (form.manager_id) payload.manager_id = form.manager_id;
        await managerService.createClient(payload);
        toast.success("Cliente criado");
      } else if (dialog === "manager") {
        await managerService.createManager(payload);
        toast.success("Gestor criado");
      } else {
        if (form.manager_id) payload.manager_id = form.manager_id;
        await managerService.createAgent(payload);
        toast.success("Agente Plus criado");
      }
      setDialog("");
      setForm(emptyForm);
      setCaps(null);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const reviewDoc = async (row, status) => {
    try {
      const note = status === "rejected" ? window.prompt("Motivo da recusa (opcional)") || "" : "";
      await documentService.review(row.id, status, note);
      toast.success(status === "approved" ? "Documento aprovado" : "Documento recusado");
      setRows((await documentService.adminList({ page: 1, per_page: 50 })).items);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const review = async (row, action, kind) => {
    try {
      if (kind === "deposits") await managerService.reviewDeposit(row.id, action);
      else await managerService.reviewWithdrawal(row.id, action);
      toast.success(action === "approve" ? "Aprovado" : "Rejeitado");
      setRows(kind === "deposits" ? await managerService.deposits() : await managerService.withdrawals());
      setSummary(await managerService.summary());
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const applyAdjust = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await managerService.adjustBalance(balanceClient.id, Number(adjust.amount),
                                         adjust.target, adjust.note, adjust.kind);
      toast.success("Saldo atualizado");
      setBalanceClient(null);
      setAdjust({ amount: "", target: "balance", note: "", kind: "real" });
      setClients(await managerService.clients(cleanFilters(filters)));
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const savePosition = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await managerService.editPosition(editPos.id, {
        side: editPos.side,
        entry_price: Number(editPos.entry_price),
        quantity: Number(editPos.quantity),
      });
      toast.success("Posição atualizada");
      setEditPos(null);
      setTrading(await managerService.tradingDetail(client));
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const hide = async (row, type) => {
    if (!window.confirm(`Eliminar ${row.name} da sua lista?`)) return;
    try {
      if (type === "client") await managerService.hideClient(row.id);
      else await managerService.hideTeamMember(row.id);
      toast.success("Registo eliminado");
      setCaps(null);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <AppShell
      nav={navForRole(user?.role)}
      title={isAgent ? "Painel do Agente Plus" : (isSupervisor ? "Painel do Supervisor" : "Painel do Gestor")}
      subtitle={isAgent ? "Os seus clientes atribuídos"
                        : (isSupervisor ? "Clientes, gestores, pagamentos e documentos da sua Brand"
                                        : "Clientes da equipa, agentes e movimentos")}
    >
      <div data-testid="manager-page" className="space-y-6">
        {(summary?.deposits_pending_count || 0) + (summary?.withdrawals_pending_count || 0) > 0 ? (
          <div data-testid="pending-alert"
               className="flex flex-wrap items-center gap-3 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-3 text-sm text-[#FBBF24]">
            <AlertTriangle className="h-4 w-4" />
            <span>
              {summary.deposits_pending_count} depósito(s) e {summary.withdrawals_pending_count} retirada(s) à espera de aprovação
              {" "}({fmtMoney(summary.deposits_pending || 0)} · {fmtMoney(summary.withdrawals_pending || 0)})
            </span>
          </div>
        ) : null}

        <div data-testid="manager-summary" className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {[
            { id: "clients", label: "Clientes", value: summary?.clients ?? 0 },
            ...(caps?.can_create_agents ? [{ id: "agents", label: "Agentes Plus", value: summary?.agents ?? 0 }] : []),
            { id: "deposits", label: "Depósitos aprovados", value: fmtMoney(summary?.deposits_total || 0) },
            { id: "deposits-pending", label: "Depósitos pendentes", value: fmtMoney(summary?.deposits_pending || 0) },
            { id: "pnl", label: "P/L realizado", value: fmtMoney(summary?.realized_pnl || 0) },
          ].map((c) => (
            <div key={c.id} data-testid={`summary-${c.id}`} className={`${card} p-4`}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{c.label}</p>
              <p className="mt-2 font-mono text-lg text-white">{c.value}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-2 rounded-md border border-[#22252F] p-1">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => { setTab(t.key); setRows([]); }} data-testid={`manager-tab-${t.key}`}
                      className={`rounded px-4 py-2 text-sm transition-colors ${
                        tab === t.key ? "bg-[#132038] text-[#60A5FA]" : "text-[#94A3B8] hover:text-white"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <button data-testid="create-client-button" onClick={() => { setDialog("client"); setForm(emptyForm); }}
                  className="ml-auto inline-flex items-center gap-2 rounded-md bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4" /> Novo cliente
          </button>
          {caps?.can_create_managers ? (
            <button data-testid="create-manager-button" onClick={() => { setDialog("manager"); setForm(emptyForm); }}
                    className="inline-flex items-center gap-2 rounded-md border border-[#22252F] px-4 py-2 text-sm text-[#94A3B8] hover:text-white">
              <Plus className="h-4 w-4" /> Novo Gestor
            </button>
          ) : null}
          {caps?.can_create_agents ? (
            <button data-testid="create-agent-button" onClick={() => { setDialog("agent"); setForm(emptyForm); }}
                    className="inline-flex items-center gap-2 rounded-md border border-[#22252F] px-4 py-2 text-sm text-[#94A3B8] hover:text-white">
              <Plus className="h-4 w-4" /> Novo Agente Plus
            </button>
          ) : null}
        </div>

        {tab === "clients" ? (
          <div className="space-y-4">
          <ClientFilters meta={meta} agents={agents} filters={filters}
                         onChange={setFilters} onReset={() => setFilters(EMPTY_FILTERS)} />
          <div className={`overflow-x-auto ${card}`}>
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead className="border-b border-[#22252F] text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
                <tr>
                  <th className="px-4 py-4">Cliente</th><th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4">Agente</th>
                  <th className="px-4 py-4 text-right">Saldo</th><th className="px-4 py-4 text-right">Depósitos</th>
                  <th className="px-4 py-4 text-right">P/L</th><th className="px-4 py-4 text-right">Posições</th>
                  <th className="px-4 py-4">Último acesso</th>
                  {caps?.can_adjust_balance || caps?.can_hide_records ? <th className="px-4 py-4 text-right">Ações</th> : null}
                </tr>
              </thead>
              <tbody data-testid="manager-clients-body">
                {clients.map((c) => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-b border-[#1A1D25] hover:bg-[#151821]">
                    <td className="px-4 py-3">
                      <Link to={`/clients/${c.id}`} data-testid={`client-profile-link-${c.id}`}
                            className="text-white transition-colors hover:text-[#60A5FA]">{c.name}</Link>
                      <p className="font-mono text-xs text-[#94A3B8]">{c.email || c.phone_full}</p>
                    </td>
                    <td className="px-4 py-3"><ClientStatusCell client={c} testId={`client-status-${c.id}`} /></td>
                    <td className="px-4 py-3 text-xs text-[#94A3B8]">{c.agent_name || "Direto"}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">{fmtMoney(c.virtual_balance)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#94A3B8]">{fmtMoney(c.deposits_total || 0)}</td>
                    <td className={`px-4 py-3 text-right font-mono ${(c.realized_pnl || 0) >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                      {fmtMoney(c.realized_pnl || 0)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[#94A3B8]">{c.open_positions}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{fmtDate(c.last_login_at)}</td>
                    {caps?.can_adjust_balance || caps?.can_hide_records ? (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {caps?.can_adjust_balance ? (
                            <button data-testid={`manager-balance-${c.id}`}
                                    onClick={() => { setBalanceClient(c); setAdjust({ amount: "", target: "balance", note: "", kind: "real" }); }}
                                    className="rounded-md border border-[#22252F] px-3 py-1 text-xs text-[#10B981] hover:border-[#10B981]/50">
                              Saldo
                            </button>
                          ) : null}
                          {caps?.can_hide_records ? (
                            <button data-testid={`hide-client-${c.id}`} onClick={() => hide(c, "client")}
                                    className="rounded-md border border-[#22252F] px-3 py-1 text-xs text-[#EF4444] hover:border-[#EF4444]/50">
                              Eliminar
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
                {clients.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[#64748B]">Sem clientes para estes filtros</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
          </div>
        ) : null}

        {tab === "team" ? (
          <div className="space-y-4">
            {caps?.can_create_managers ? (
              <div className={`${card} p-4`} data-testid="manager-managers-list">
                <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Gestores</p>
                {managers.map((m) => (
                  <div key={m.id} data-testid={`manager-row-${m.id}`}
                       className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1A1D25] py-3 text-sm">
                    <Link to={`/team/${m.id}`} data-testid={`manager-profile-link-${m.id}`}
                          className="text-white transition-colors hover:text-[#60A5FA]">{m.name}</Link>
                    <span className="font-mono text-xs text-[#94A3B8]">{m.email || m.phone_full}</span>
                    <span className="font-mono text-xs text-[#60A5FA]">{m.agents} agente(s) · {m.clients} cliente(s)</span>
                    <span className="font-mono text-xs uppercase text-[#94A3B8]">{m.status}</span>
                    {caps?.can_hide_records ? (
                      <button data-testid={`hide-manager-${m.id}`} onClick={() => hide(m, "team")}
                              className="rounded-md border border-[#22252F] px-3 py-1 text-xs text-[#EF4444] hover:border-[#EF4444]/50">
                        Eliminar
                      </button>
                    ) : null}
                  </div>
                ))}
                {managers.length === 0 ? <p className="py-6 text-center text-sm text-[#64748B]">Sem gestores</p> : null}
              </div>
            ) : null}
            <div className={`${card} p-4`} data-testid="manager-agents-list">
              <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Agentes Plus</p>
              {agents.map((a) => (
                <div key={a.id} data-testid={`agent-row-${a.id}`}
                     className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1A1D25] py-3 text-sm">
                  <Link to={`/team/${a.id}`} data-testid={`agent-profile-link-${a.id}`}
                        className="text-white transition-colors hover:text-[#60A5FA]">{a.name}</Link>
                  <span className="font-mono text-xs text-[#94A3B8]">{a.email || a.phone_full}</span>
                  <span className="font-mono text-xs text-[#60A5FA]">{a.clients} cliente(s)</span>
                  <span className="font-mono text-xs uppercase text-[#94A3B8]">{a.status}</span>
                  {caps?.can_hide_records ? (
                    <button data-testid={`hide-agent-${a.id}`} onClick={() => hide(a, "team")}
                            className="rounded-md border border-[#22252F] px-3 py-1 text-xs text-[#EF4444] hover:border-[#EF4444]/50">
                      Eliminar
                    </button>
                  ) : null}
                </div>
              ))}
              {agents.length === 0 ? <p className="py-6 text-center text-sm text-[#64748B]">Sem Agentes Plus</p> : null}
            </div>
          </div>
        ) : null}

        {tab === "trades" ? (
          <div className="space-y-4">
            <select data-testid="manager-client-select" value={client || ""} onChange={(e) => setClient(e.target.value)}
                    className={`${input} max-w-md`}>
              <option value="">Escolher cliente</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.email || c.phone_full}</option>)}
            </select>
            <div className={`${card} p-4`} data-testid="manager-trades-list">
              {rows.map((r) => (
                <div key={r.id} className="flex justify-between border-b border-[#1A1D25] py-2 font-mono text-xs text-[#94A3B8]">
                  <span className="text-white">{r.symbol} {r.side}</span>
                  <span>{r.quantity} @ {r.execution_price}</span>
                  <span>{fmtMoney(r.total_value)}</span>
                  <span>{fmtDate(r.created_at)}</span>
                </div>
              ))}
              {rows.length === 0 ? <p className="py-8 text-center text-sm text-[#64748B]">Sem operações</p> : null}
            </div>

            {caps?.can_edit_positions && client ? (
              <div className={`${card} p-4`} data-testid="manager-open-positions">
                <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                  Posições abertas · edição permitida pelo Admin
                </p>
                {(trading?.open_positions || []).map((p) => (
                  <div key={p.id} data-testid={`position-row-${p.id}`}
                       className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1A1D25] py-3 font-mono text-xs text-[#94A3B8]">
                    <span className="text-white">{p.symbol}</span>
                    <span className={p.side === "buy" ? "text-[#10B981]" : "text-[#EF4444]"}>{p.side}</span>
                    <span>{p.quantity} @ {p.entry_price}</span>
                    <span>1:{p.leverage}</span>
                    <span className={Number(p.pnl) >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}>{fmtMoney(p.pnl)}</span>
                    <button data-testid={`edit-position-${p.id}`}
                            onClick={() => setEditPos({ id: p.id, symbol: p.symbol, side: p.side,
                                                        entry_price: p.entry_price, quantity: p.quantity })}
                            className="rounded-md border border-[#22252F] px-3 py-1 text-[#60A5FA] hover:border-[#60A5FA]/50">
                      Editar
                    </button>
                  </div>
                ))}
                {!(trading?.open_positions || []).length ? (
                  <p className="py-6 text-center text-sm text-[#64748B]">Sem posições abertas</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === "documents" ? (
          <div className={`${card} p-4`} data-testid="manager-documents-list">
            {rows.map((d) => (
              <div key={d.id} data-testid={`document-row-${d.id}`}
                   className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1A1D25] py-3 text-sm">
                <span className="text-white">{d.user_name || d.user_email}</span>
                <span className="text-xs text-[#94A3B8]">{d.document_number || "—"}</span>
                <span className="font-mono text-[10px] text-[#64748B]">{d.kind}</span>
                <span className="font-mono text-xs uppercase text-[#60A5FA]">{d.status}</span>
                <span className="flex gap-2">
                  {d.status === "pending" ? (
                    <>
                      <button data-testid={`approve-document-${d.id}`} onClick={() => reviewDoc(d, "approved")}
                              className="rounded-md border border-[#22252F] p-2 text-[#10B981] hover:border-[#10B981]/50">
                        <Check className="h-4 w-4" />
                      </button>
                      <button data-testid={`reject-document-${d.id}`} onClick={() => reviewDoc(d, "rejected")}
                              className="rounded-md border border-[#22252F] p-2 text-[#EF4444] hover:border-[#EF4444]/50">
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : null}
                </span>
              </div>
            ))}
            {rows.length === 0 ? <p className="py-8 text-center text-sm text-[#64748B]">Sem documentos</p> : null}
          </div>
        ) : null}

        {tab === "deposits" || tab === "withdrawals" ? (
          <div className={`${card} p-4`} data-testid={`manager-${tab}-list`}>
            {rows.map((r) => (
              <div key={r.id} data-testid={`${tab}-row-${r.id}`}
                   className="flex flex-wrap items-center justify-between gap-2 border-b border-[#1A1D25] py-3 text-sm">
                <span className="font-mono text-white">{fmtMoney(r.amount)}</span>
                <span className="text-xs text-[#94A3B8]">{r.user_email}</span>
                <span className="text-xs text-[#94A3B8]">{r.method_label || r.account_label}</span>
                <span className="font-mono text-xs uppercase text-[#60A5FA]">{r.status}</span>
                <span className="font-mono text-xs text-[#94A3B8]">{fmtDate(r.created_at)}</span>
                {caps?.can_review_payments && r.status === "pending" ? (
                  <span className="flex gap-2">
                    <button data-testid={`approve-${tab}-${r.id}`} onClick={() => review(r, "approve", tab)}
                            className="rounded-md border border-[#22252F] p-2 text-[#10B981] hover:border-[#10B981]/50">
                      <Check className="h-4 w-4" />
                    </button>
                    <button data-testid={`reject-${tab}-${r.id}`} onClick={() => review(r, "reject", tab)}
                            className="rounded-md border border-[#22252F] p-2 text-[#EF4444] hover:border-[#EF4444]/50">
                      <X className="h-4 w-4" />
                    </button>
                  </span>
                ) : null}
              </div>
            ))}
            {rows.length === 0 ? <p className="py-8 text-center text-sm text-[#64748B]">Sem registos</p> : null}
          </div>
        ) : null}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog("")}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="create-team-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light tracking-tight">
              {dialog === "agent" ? "Novo Agente Plus" : (dialog === "manager" ? "Novo Gestor" : "Novo cliente")}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#94A3B8]">
              Preencha os dados de acesso. O registo fica na sua Brand.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="flex gap-2 rounded-md border border-[#22252F] p-1">
              {["email", "phone"].map((m) => (
                <button key={m} type="button" data-testid={`team-mode-${m}`} onClick={() => setMode(m)}
                        className={`flex-1 rounded px-3 py-2 text-xs uppercase tracking-[0.15em] ${
                          mode === m ? "bg-[#2563EB] text-white" : "text-[#94A3B8]"}`}>
                  {m === "email" ? "Email" : "Telefone"}
                </button>
              ))}
            </div>
            <input required data-testid="team-name-input" placeholder="Nome" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
            {mode === "phone" ? (
              <PhoneField required dial={form.phone_dial} phone={form.phone}
                          onDial={(v) => setForm({ ...form, phone_dial: v })}
                          onPhone={(v) => setForm({ ...form, phone: v })} testIdPrefix="team-phone" />
            ) : (
              <input required type="email" data-testid="team-email-input" placeholder="nome@dominio.com" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
            )}
            <input required minLength={6} type="password" data-testid="team-password-input" placeholder="Palavra-passe"
                   value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
            {dialog === "client" ? (
              <>
                <input type="number" data-testid="team-balance-input" placeholder="Saldo inicial (USD)"
                       value={form.virtual_balance} onChange={(e) => setForm({ ...form, virtual_balance: e.target.value })}
                       className={`${input} font-mono`} />
                {caps?.can_create_agents && agents.length ? (
                  <select data-testid="team-agent-select" value={form.agent_id}
                          onChange={(e) => setForm({ ...form, agent_id: e.target.value })} className={input}>
                    <option value="">Cliente direto do gestor</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                ) : null}
              </>
            ) : null}
            {dialog !== "manager" && caps?.can_create_managers && managers.length ? (
              <select data-testid="team-manager-select" value={form.manager_id}
                      onChange={(e) => setForm({ ...form, manager_id: e.target.value })} className={input}>
                <option value="">Sem gestor responsável</option>
                {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            ) : null}
            <button type="submit" disabled={busy} data-testid="team-submit-button"
                    className="h-11 w-full rounded-md bg-[#2563EB] text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60">
              Criar
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!balanceClient} onOpenChange={(o) => !o && setBalanceClient(null)}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="manager-balance-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light tracking-tight">Ajustar saldo</DialogTitle>
            <DialogDescription className="text-xs text-[#94A3B8]">
              Indique o montante, o motivo e se o valor é real ou inflação.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={applyAdjust} className="space-y-3">
            <p className="text-sm text-[#94A3B8]">
              {balanceClient?.name} · atual {fmtMoney(balanceClient?.virtual_balance || 0)}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[{ k: "balance", l: "Saldo" }, { k: "credit", l: "Crédito" }].map((o) => (
                <button key={o.k} type="button" data-testid={`adjust-target-${o.k}`}
                        onClick={() => setAdjust({ ...adjust, target: o.k })}
                        className={`h-11 rounded-md border text-sm transition-colors ${
                          adjust.target === o.k ? "border-[#2563EB] bg-[#2563EB]/15 text-white"
                                                : "border-[#22252F] text-[#94A3B8] hover:text-white"}`}>
                  {o.l}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[{ k: "real", l: "Real" }, { k: "inflacao", l: "Inflação" }].map((o) => (
                <button key={o.k} type="button" data-testid={`adjust-kind-${o.k}`}
                        onClick={() => setAdjust({ ...adjust, kind: o.k })}
                        className={`h-11 rounded-md border text-sm transition-colors ${
                          adjust.kind === o.k ? "border-[#F59E0B] bg-[#F59E0B]/15 text-white"
                                              : "border-[#22252F] text-[#94A3B8] hover:text-white"}`}>
                  {o.l}
                </button>
              ))}
            </div>
            <input required type="number" step="0.01" data-testid="adjust-amount-input"
                   placeholder="Montante (negativo para retirar)" value={adjust.amount}
                   onChange={(e) => setAdjust({ ...adjust, amount: e.target.value })} className={`${input} font-mono`} />
            <input required={isSupervisor} data-testid="adjust-note-input" placeholder="Motivo do ajuste"
                   value={adjust.note} onChange={(e) => setAdjust({ ...adjust, note: e.target.value })} className={input} />
            <button type="submit" disabled={busy} data-testid="adjust-submit-button"
                    className="h-11 w-full rounded-md bg-[#10B981] text-sm font-semibold text-[#04150F] hover:bg-[#0EA271] disabled:opacity-60">
              Aplicar
            </button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editPos} onOpenChange={(o) => !o && setEditPos(null)}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="edit-position-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light tracking-tight">Editar posição</DialogTitle>
            <DialogDescription className="text-xs text-[#94A3B8]">
              {editPos?.symbol} · alterações registadas na auditoria
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={savePosition} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {[{ k: "buy", l: "Compra" }, { k: "sell", l: "Venda" }].map((o) => (
                <button key={o.k} type="button" data-testid={`position-side-${o.k}`}
                        onClick={() => setEditPos({ ...editPos, side: o.k })}
                        className={`h-11 rounded-md border text-sm transition-colors ${
                          editPos?.side === o.k ? "border-[#2563EB] bg-[#2563EB]/15 text-white"
                                                : "border-[#22252F] text-[#94A3B8] hover:text-white"}`}>
                  {o.l}
                </button>
              ))}
            </div>
            <input required type="number" step="0.00000001" data-testid="position-entry-input"
                   placeholder="Preço de entrada" value={editPos?.entry_price ?? ""}
                   onChange={(e) => setEditPos({ ...editPos, entry_price: e.target.value })}
                   className={`${input} font-mono`} />
            <input required type="number" step="0.00000001" data-testid="position-quantity-input"
                   placeholder="Quantidade" value={editPos?.quantity ?? ""}
                   onChange={(e) => setEditPos({ ...editPos, quantity: e.target.value })}
                   className={`${input} font-mono`} />
            <button type="submit" disabled={busy} data-testid="position-save-button"
                    className="h-11 w-full rounded-md bg-[#2563EB] text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60">
              Guardar
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
